import React, { useState } from 'react';
import { PrimeArtifactState } from '../../types/primeModel';
import { PrimeArtifactStateTransition } from '../../types/primeModel';
import { DemandType, DemandFlowType, ArtifactStateTransitionType } from '../../types/enums';
import styles from './StateTransitionGenerator.module.css';

interface StateTransitionGeneratorProps {
  states: PrimeArtifactState[];
  artifactId: string;
  onGenerate: (transitions: PrimeArtifactStateTransition[]) => void;
  onCancel: () => void;
}

export const StateTransitionGenerator: React.FC<StateTransitionGeneratorProps> = ({
  states,
  artifactId,
  onGenerate,
  onCancel,
}) => {
  const [selectedStates, setSelectedStates] = useState<PrimeArtifactState[]>([]);

  const handleStateSelect = (state: PrimeArtifactState) => {
    if (selectedStates.includes(state)) {
      setSelectedStates(selectedStates.filter((s) => s !== state));
    } else {
      setSelectedStates([...selectedStates, state]);
    }
  };

  const generateTransitions = () => {
    const transitions: Omit<PrimeArtifactStateTransition, 'id'>[] = [];

    for (let i = 0; i < selectedStates.length - 1; i++) {
      const fromState = selectedStates[i];
      const toState = selectedStates[i + 1];

      const transition: Omit<PrimeArtifactStateTransition, 'id'> = {
        name: `${fromState.name} to ${toState.name}`,
        description: `Transition from ${fromState.name} to ${toState.name}`,
        prime_artifact_ref: artifactId,
        from_state_ref: fromState.id,
        to_state_ref: toState.id,
        prime_process_ref: '',
        default_transition_type: ArtifactStateTransitionType.OPERATIONAL,
        is_secondary: false,
        transition_parameters: {},
        validation_rules: {},
        demand_type: DemandType.TARGET,
        demand_flow_type: DemandFlowType.DRUM,
        improvement_parameters: {},
        improvement_ref: null,
        is_improvement: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      transitions.push(transition);
    }

    onGenerate(transitions as PrimeArtifactStateTransition[]);
  };

  return (
    <div className={styles.stateTransitionGenerator}>
      <h3>Generate State Transitions</h3>
      <p>Select states in order to generate transitions between them:</p>
      <div className={styles.stateList}>
        <ul>
          {states.map((state) => (
            <li
              key={state.id}
              className={`${selectedStates.includes(state) ? styles.selected : ''}`}
              onClick={() => handleStateSelect(state)}
            >
              <span className={styles.stateName}>{state.name}</span>
              {state.description && <p className={styles.stateDescription}>{state.description}</p>}
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.generatorActions}>
        <button
          onClick={generateTransitions}
          disabled={selectedStates.length < 2}
          className={styles.generateButton}
        >
          Generate Transitions
        </button>
        <button onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default StateTransitionGenerator;
